import { API_URL, PRODUCTION_API_URL, STAGING_API_URL, loginTarget, saveAuthSession } from '@/lib/apiConfig';
import AppleSignInButton from '@/components/AppleSignInButton';
import PushSignInOption from '@/components/PushSignInOption';
import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Loader2, ArrowLeft, Eye, EyeOff } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import { msalService } from "@/lib/msalService";
import { isNativeApp, nativeRequestHeaders } from "@/lib/native";
import { startNativeLogin } from "@/lib/nativeAuth";
import Turnstile, { useTurnstile } from "@/components/Turnstile";


export default function Login() {
  const [searchParams] = useSearchParams();
  const nativeApp = isNativeApp();
  const loginShellRef = useRef(null);
  const challengeTarget = useRef(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isPasswordRevealed, setIsPasswordRevealed] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [msalLoading, setMsalLoading] = useState(false);
  const [msalReady, setMsalReady] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [totpCode, setTotpCode] = useState("");
  const [challengeToken, setChallengeToken] = useState("");
  const [pushApprovalAvailable, setPushApprovalAvailable] = useState(false);
  const turnstile = useTurnstile();
  const loginBlockedReason = turnstile.loading
    ? "Loading security check..."
    : turnstile.failed
      ? ""
      : turnstile.enabled && !turnstile.token
        ? "Security check will run when you sign in."
        : "";

  useEffect(() => {
    if (nativeApp) {
      setMsalReady(true);
      const rootStyle = document.documentElement.style;
      const inputSelector = 'input, textarea, select, [contenteditable="true"]';
      let scrollTimer;
      let nativeKeyboardHeight = 0;
      let keyboardShowListener;
      let keyboardHideListener;

      const activeTextInput = () => {
        const active = document.activeElement;
        return active instanceof HTMLElement && active.matches(inputSelector) ? active : null;
      };

      const scrollActiveInputIntoView = (delay = 0) => {
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(() => {
          activeTextInput()?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
        }, delay);
      };

      const syncViewport = () => {
        const viewport = window.visualViewport;
        const viewportHeight = Math.round(viewport?.height || window.innerHeight);
        const visualKeyboardInset = Math.max(
          0,
          Math.round(window.innerHeight - viewportHeight - (viewport?.offsetTop || 0))
        );
        const keyboardInset = Math.max(nativeKeyboardHeight, visualKeyboardInset);

        rootStyle.setProperty('--app-viewport-height', `${viewportHeight}px`);
        rootStyle.setProperty('--native-keyboard-inset', `${keyboardInset}px`);

        const keyboardOpen = keyboardInset > 80 && !!activeTextInput();
        document.body.classList.toggle('native-keyboard-open', keyboardOpen);
        if (keyboardOpen) scrollActiveInputIntoView(80);
      };

      const onFocus = (e) => {
        if (e.target instanceof HTMLElement && e.target.matches(inputSelector)) {
          document.body.classList.add('native-input-focused');
          syncViewport();
          scrollActiveInputIntoView(300);
        }
      };
      const onBlur = () => {
        window.setTimeout(() => {
          if (!activeTextInput()) {
            document.body.classList.remove('native-input-focused', 'native-keyboard-open');
            nativeKeyboardHeight = 0;
            rootStyle.setProperty('--native-keyboard-inset', '0px');
            syncViewport();
          }
        }, 50);
      };

      const addKeyboardListener = (eventName, handler, assignListener) => {
        const keyboard = window.Capacitor?.Plugins?.Keyboard;
        if (!keyboard?.addListener) return;

        const listener = keyboard.addListener(eventName, handler);
        if (typeof listener?.then === 'function') {
          listener.then(assignListener).catch(() => {});
        } else {
          assignListener(listener);
        }
      };

      addKeyboardListener('keyboardWillShow', (info) => {
        nativeKeyboardHeight = Math.max(0, Math.round(info?.keyboardHeight || 0));
        document.body.classList.add('native-keyboard-open');
        syncViewport();
        scrollActiveInputIntoView(80);
      }, (listener) => {
        keyboardShowListener = listener;
      });

      addKeyboardListener('keyboardWillHide', () => {
        nativeKeyboardHeight = 0;
        document.body.classList.remove('native-keyboard-open');
        syncViewport();
      }, (listener) => {
        keyboardHideListener = listener;
      });

      window.visualViewport?.addEventListener('resize', syncViewport);
      window.visualViewport?.addEventListener('scroll', syncViewport);
      window.addEventListener('resize', syncViewport);
      document.addEventListener('focusin', onFocus);
      document.addEventListener('focusout', onBlur);
      syncViewport();

      return () => {
        window.clearTimeout(scrollTimer);
        window.visualViewport?.removeEventListener('resize', syncViewport);
        window.visualViewport?.removeEventListener('scroll', syncViewport);
        window.removeEventListener('resize', syncViewport);
        keyboardShowListener?.remove?.();
        keyboardHideListener?.remove?.();
        document.removeEventListener('focusin', onFocus);
        document.removeEventListener('focusout', onBlur);
        document.body.classList.remove('native-input-focused', 'native-keyboard-open');
        rootStyle.removeProperty('--app-viewport-height');
        rootStyle.removeProperty('--native-keyboard-inset');
      };
    }
    (async () => {
      try {
        await msalService.initialize();
        setMsalReady(true);

        if (sessionStorage.getItem('explicit_logout')) {
          sessionStorage.removeItem('explicit_logout');
          return;
        }

        if (window.location.origin === STAGING_API_URL) return;
        const account = msalService.getAccount();
        if (account) {
          const exchangeKey = 'msal_auto_exchange_attempted';
          if (sessionStorage.getItem(exchangeKey)) {
            sessionStorage.removeItem(exchangeKey);
            return;
          }
          sessionStorage.setItem(exchangeKey, '1');
          const loginToken = msalService.consumeLoginToken();
          await handleMsalExchange(loginToken);
        }
      } catch (err) {
        console.error('MSAL init error:', err);
      }
    })();
  }, [nativeApp]);

  const redirectAfterLogin = () => {
    const returnUrl = searchParams.get('returnUrl') || '/';
    window.location.href = returnUrl;
  };

  const handleSubmit = async (e, otpCode) => {
    if (e) e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const turnstileToken = await turnstile.getToken();
      const target = otpCode && challengeTarget.current ? challengeTarget.current : loginTarget(email);
      const body = { email: email.trim(), password };
      if (otpCode) {
        body.totp_code = otpCode;
        if (challengeToken) body.challenge_token = challengeToken;
      }
      if (turnstileToken) body.turnstile_token = turnstileToken;

      const res = await fetch(`${target.apiUrl}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...nativeRequestHeaders() },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({ message: 'Login failed' }));
        throw new Error(data.message || data.error || 'Login failed');
      }

      const data = await res.json();

      if (data.requires_2fa) {
        challengeTarget.current = target;
        setRequires2FA(true);
        setPushApprovalAvailable(!!data.push_approval_available);
        setTotpCode("");
        if (data.challenge_token) setChallengeToken(data.challenge_token);
        setLoading(false);
        return;
      }

      saveAuthSession(data.token, target.environment);
      redirectAfterLogin();
    } catch (err) {
      setError(err.message || 'Login failed');
      turnstile.reset();
    } finally {
      setLoading(false);
    }
  };

  const handleMsalExchange = async (accessToken) => {
    setMsalLoading(true);
    setError("");
    try {
      const token = accessToken || await msalService.getAccessToken();
      if (!token) return;

      const account = msalService.getAccount();
      const res = await fetch(`${nativeApp ? PRODUCTION_API_URL : API_URL}/auth/exchange-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          microsoftToken: token,
          userInfo: { email: account.username, name: account.name || account.username }
        })
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({ message: 'Microsoft login failed' }));
        throw new Error(data.message || 'Microsoft login failed');
      }

      const data = await res.json();
      saveAuthSession(data.token, 'production');
      redirectAfterLogin();
    } catch (err) {
      setError(err.message || 'Microsoft login failed');
    } finally {
      setMsalLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    if (!nativeApp && window.location.origin === STAGING_API_URL) {
      window.location.href = `${PRODUCTION_API_URL}/login`;
      return;
    }
    setMsalLoading(true);
    setError("");
    try {
      if (isNativeApp()) {
        const token = await startNativeLogin();
        saveAuthSession(token, 'production');
        redirectAfterLogin();
      } else {
        await msalService.login();
      }
    } catch (err) {
      if (err.message !== 'cancelled') {
        setError(err.message || 'Microsoft login failed');
      }
      setMsalLoading(false);
    }
  };

  return (
    <div
      ref={loginShellRef}
      className={`flex items-center justify-center p-6 ${nativeApp ? 'native-login-screen bg-white overflow-y-auto' : 'min-h-screen bg-slate-50'}`}
    >
      <Card className={`max-w-md w-full ${nativeApp ? 'native-login-card border-0 shadow-none' : 'border-2 border-slate-200'}`}>
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className="bg-black rounded-xl px-4 py-2">
              <BrandLogo alt="Seiko Sports Technologies" className="h-8 w-auto object-contain" />
            </div>
          </div>
          <CardTitle className="text-2xl">{requires2FA ? "Two-Factor Authentication" : "Sign In"}</CardTitle>
          <p className="text-slate-600 text-sm mt-1">
            {requires2FA ? "Enter the 6-digit code from your authenticator app" : "Sign in to your account"}
          </p>
        </CardHeader>
        <CardContent className="p-6 pt-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {requires2FA ? (
            <div className="space-y-6">
              <div className="flex justify-center">
                <InputOTP
                  id="totp_code"
                  name="totp_code"
                  maxLength={6}
                  value={totpCode}
                  onChange={(val) => setTotpCode(val)}
                  onComplete={(val) => handleSubmit(null, val)}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  pattern="^[0-9]+$"
                  enterKeyHint="go"
                  aria-label="Two-factor authentication code"
                  autoFocus
                  pasteTransformer={(pasted) => pasted.replace(/\D/g, "")}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button
                onClick={() => handleSubmit(null, totpCode)}
                disabled={loading || totpCode.length !== 6}
                className="w-full h-11 bg-[rgb(var(--brand-yellow))] hover:bg-[rgb(var(--brand-yellow-hover))] text-black font-semibold"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  "Verify"
                )}
              </Button>

              {pushApprovalAvailable && challengeToken && <PushSignInOption
                key={challengeToken}
                apiUrl={challengeTarget.current.apiUrl}
                challengeToken={challengeToken}
                onApproved={(token) => { saveAuthSession(token, challengeTarget.current.environment); redirectAfterLogin(); }}
              />}
              <button
                type="button"
                onClick={() => { setRequires2FA(false); setTotpCode(""); setChallengeToken(""); setError(""); }}
                className="w-full text-sm text-slate-600 hover:text-slate-900 flex items-center justify-center gap-1"
              >
                <ArrowLeft className="w-3 h-3" />
                Back to sign in
              </button>
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="username"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    autoComplete="username"
                    inputMode="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    enterKeyHint="next"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={isPasswordRevealed ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      enterKeyHint="go"
                      className="pr-12"
                    />
                    <button
                      type="button"
                      aria-label={isPasswordRevealed ? "Release to hide password" : "Hold to show password"}
                      title="Hold to show password"
                      onPointerDown={(event) => {
                        event.preventDefault();
                        setIsPasswordRevealed(true);
                      }}
                      onPointerUp={() => setIsPasswordRevealed(false)}
                      onPointerCancel={() => setIsPasswordRevealed(false)}
                      onPointerLeave={() => setIsPasswordRevealed(false)}
                      onKeyDown={(event) => {
                        if (event.key === " " || event.key === "Enter") {
                          event.preventDefault();
                          setIsPasswordRevealed(true);
                        }
                      }}
                      onKeyUp={() => setIsPasswordRevealed(false)}
                      onBlur={() => setIsPasswordRevealed(false)}
                      onContextMenu={(event) => event.preventDefault()}
                      className="absolute inset-y-0 right-0 flex w-11 touch-manipulation select-none items-center justify-center text-slate-400 transition-colors hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-500"
                    >
                      {isPasswordRevealed ? (
                        <EyeOff className="h-4 w-4" aria-hidden="true" />
                      ) : (
                        <Eye className="h-4 w-4" aria-hidden="true" />
                      )}
                    </button>
                  </div>
                </div>

                <Turnstile turnstile={turnstile} />

                <Button
                  type="submit"
                  disabled={loading || msalLoading || turnstile.loading}
                  className="w-full h-11 bg-[rgb(var(--brand-yellow))] hover:bg-[rgb(var(--brand-yellow-hover))] text-black font-semibold"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    "Sign In"
                  )}
                </Button>
                {loginBlockedReason && !loading && !msalLoading && (
                  <p className="text-center text-sm text-slate-600">{loginBlockedReason}</p>
                )}
              </form>

              {msalReady && (
                <>
                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-2 text-slate-500">or</span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleMicrosoftLogin}
                    disabled={msalLoading || loading}
                    className="w-full h-11"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 21 21" className="mr-2">
                      <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                      <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                      <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                      <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                    </svg>
                    {msalLoading ? 'Signing in...' : 'Sign in with Microsoft'}
                  </Button>
                </>
              )}

              <div className="mt-3"><AppleSignInButton /></div>

              <div className="text-center mt-4 space-y-2">
                <p className="text-sm text-slate-600">
                  <a href="/forgot-password" className="hover:text-slate-900 underline">
                    Forgot your password?
                  </a>
                </p>
                <p className="text-sm text-slate-600">
                  Don't have an account?{" "}
                  <a href="/register" className="hover:text-slate-900 underline">
                    Register
                  </a>
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
